import React, { useCallback, useEffect, useRef, useState } from "react";
import { Tag } from "antd";
import MapBase from "./MapBase";
import useMapProps from "./useMapProps";
import { MapPropsProvider } from "./MapPropsContext";
import WidgetTable from "./widgets/WidgetTable";
import WidgetTableClicked from "./widgets/WidgetTableClicked";
import { formatUid, getApiUrl } from "../../utils";
import WidgetLegend from "./widgets/WidgetLegend";
import WidgetContainer from "./widgets/WidgetContainer";
import WidgetDistrictSelect from "./widgets/WidgetDistrictSelect";
import WidgetUidWithShortcut from "./widgets/WidgetUidWithShortcut";
import WidgetToolbar from "./widgets/WidgetToolbar";
import { TI_SHOW_LIST_BUTTON } from "../../Constants";
import EllipsisText from "./widgets/EllipsisText";
import { WidgetButton } from "../../styled-components/Button";

const INITIAL_VIEW_STATE = {
  zoom: 8,
};

const ICON_MAPPING = {
  Y: ["광역", "광역"],
  N: ["지역", "지역"],
};

const ICON_LEGEND = Object.values(ICON_MAPPING);
const TRANSPARENT_COLOR = [255, 255, 255, 1];

const parseFrequency = (text) => {
  const matched = /(\d+\.\d+MHz)/i.exec(text);
  const [tx, rx] = matched || [];
  return (
    <>
      {tx && <Tag color="green">TX: {tx}</Tag>}
      {rx && <Tag color="blue">RX: {rx}</Tag>}
      {!tx && !rx && "-"}
    </>
  );
};

const MapAmateur = () => {
  const [isTableVisible, setTableVisible] = useState(false);
  const mapProps = useMapProps({
    INITIAL_POINT_LAYER_PROPS: {
      getIconCode: ({ category_code }) => {
        try {
          return ICON_MAPPING[category_code][0];
        } catch (e) {
          console.error("not found iconCode for", category_code);
          return null;
        }
      },
      getIconWidth: (feature, zoom) => (zoom < 14 ? 32 : 48),
    },
    INITIAL_VIEW_STATE,
    INITIAL_GEOJSON_POLYGON_LAYER_PROPS: {
      getLevelFillColor: () => {
        return TRANSPARENT_COLOR;
      },
    },
  });

  const {
    clickedPointIconObjects,
    setClickedPointIconObjects,
    setCurrentDistrictCode,
    clickedPolygonGeojsonObjects,
  } = mapProps;

  // 본 지도의 데이터에는 법정동코드 정보가 없어서, currentDistrictCode별로 필터링이 불가합니다.

  const onClickedPointInWidgetTable = useCallback(
    (feature) => {
      setTimeout(() => {
        if (closeButtonRef.current) {
          closeButtonRef.current.focus();
        }
      }, 3000);

      const { latitude, longitude } = feature;
      const newFeature = { ...feature, position: [longitude, latitude] };
      setClickedPointIconObjects([newFeature]);
    },
    [setClickedPointIconObjects]
  );

  useEffect(() => {
    if (clickedPolygonGeojsonObjects.length > 0) {
      const firstLayer = clickedPolygonGeojsonObjects[0];
      const {
        properties: { pk },
      } = firstLayer;
      setCurrentDistrictCode(pk);
    }
  }, [clickedPolygonGeojsonObjects, setCurrentDistrictCode]);

  const closeButtonRef = useRef();

  return (
    <MapPropsProvider value={mapProps}>
      <MapBase>
        <WidgetContainer>
          <WidgetDistrictSelect isShowDistrictLayerFromGeoserver={true} />
        </WidgetContainer>

        <WidgetTableClicked
          closeButtonRef={closeButtonRef}
          fetchUrl={getApiUrl({ type: "type2" })}
          getTitle={(objects) => `${objects.length}개의 아마추어 무선국`}
          objects={clickedPointIconObjects}
          getRow={({ uid, name, address, frequency }) => (
            <table>
              <tbody>
                <tr>
                  <th>허가번호</th>
                  <td>{formatUid(uid)}</td>
                </tr>
                <tr>
                  <th>호출명칭</th>
                  <td>{name}</td>
                </tr>
                <tr>
                  <th>주파수</th>
                  <td>{parseFrequency(frequency)}</td>
                </tr>
                <tr>
                  <th>지역</th>
                  <td>{address}</td>
                </tr>
              </tbody>
            </table>
          )}
        />

        <WidgetTable
          visible={isTableVisible}
          setVisible={setTableVisible}
          fetchUrl={getApiUrl({ type: "type1" })}
          getTitle={(layer) => `${layer.length}개의 아마추어 무선국`}
          widgetTabIndex={TI_SHOW_LIST_BUTTON}
          columns={[
            {
              title: "순번",
              dataIndex: "index",
              key: "index",
              width: 75,
              align: "center",
              // sorter: makeTableSorterFn("index"),
              fixed: "left",
            },
            {
              title: "허가번호",
              dataIndex: "uid",
              key: "uid",
              width: 200,
              fixed: "left",
              // render: (uid, feature) =>
              //   WidgetUidWithShortcut({
              //     feature,
              //     onClick: onClickedPointInWidgetTable,
              //   }),
              // sorter: (a, b) => a.uid - b.uid,
            },
            {
              title: "이름",
              dataIndex: "name",
              key: "name",
              width: 120,
              // sorter: makeTableSorterFn("name"),
            },
            {
              title: "구분",
              dataIndex: "region_name",
              key: "region_name",
              width: 80,
              // sorter: makeTableSorterFn("region_name"),
            },
            {
              title: "설(상)치장소",
              dataIndex: "address",
              key: "address",
              render: (address) => (
                <WidgetButton>
                  <EllipsisText text={address} />
                </WidgetButton>
              ),
              // sorter: makeTableSorterFn("address"),
            },
            {
              title: "주파수",
              dataIndex: "frequency",
              key: "frequency",
              width: 260,
              render: (text) => parseFrequency(text),
            },
          ]}
        />

        <WidgetLegend iconLegend={ICON_LEGEND} />
      </MapBase>
      <WidgetToolbar />
    </MapPropsProvider>
  );
};

export default MapAmateur;
